import React from "react";
import { Button } from "react-bootstrap";
import Dialog from "../../../../util/Dialog";

import CatalogContext from "../../../../contexts/CatalogContext";
import { useProposalContext } from "../../../../hooks/useProposalContext";
import ProposalItem from "../../../../models/ProposalItem";

import { processImagePath, trackEvent } from "../../../../util/Functions";

import "./ProposalItemActions.scss";
import { AxiosError } from "axios";
import { SidebarContentType, useSidebarContext } from "../../../../hooks/useSidebarContext";
import QuantityInput from "./QuantityInput";
import NotesInput from "./NotesInput";
import Proposal from "../../../../models/Proposal";
import { ProposalSelector } from "../item/gallery/ItemsSelect";
import { useDialogContext } from "../../../../hooks/useDialogContext";
import LoadMask from "../../../../components/LoadMask";

export default function ProposalItemActions({ item, mode, inOrderIcon, savedInOrderIcon, isLocked }) {
	const [catalog] = React.useContext(CatalogContext);
	const access = catalog.options?.proposals?.access;
	const {
		currentProposal,
		flag,
		setCurrentProposal,
		fetchAndSetCurrent,
		createNewProposal,
		forceUpdateRerenderFlag,
	} = useProposalContext();
	
	const SidebarContext = useSidebarContext();
	const [isSaving, setIsSaving] = React.useState(false);
	const [addingToProposal, setAddingToProposal] = React.useState(undefined);
	const [loadingMessage, setLoadingMessage] = React.useState(undefined);
	const DialogContext = useDialogContext();
	
	let proposalItem;
	if (item) {
		const item_id = +item?.id;
		proposalItem = item?.id && currentProposal?.items?.find(cur => +(cur.item?.id || cur.item) === item_id);

		if (!proposalItem) {
			proposalItem = new ProposalItem();
			proposalItem.item = item?.id;
			proposalItem.proposal = currentProposal?.id;
		}
	}

	/*
		TODO !!!!!!!!!!! faking it until stones have Inventory records and item has Item.inventory_dependent and Item.total_inventory
		don't check inventory now because NULL should be considered infinite whereas 0 is no inventory
		maybe some sort of "can oversell" flag can be added if inventory becomes mandatory
	 */
	const isStone = !!proposalItem?.item?.gemstone;
	const maximumQuantity = isStone ? 1 : undefined;
	const isSingleUse = maximumQuantity === 1;

	const [quantityDisplay, setQuantityDisplay] = React.useState(proposalItem?.quantity);

	const aboutToUpdateQuantity = React.useRef(undefined);
	const aboutToUpdateDebounce = React.useRef(undefined);

	React.useEffect(() => {
		proposalItem && setQuantityDisplay(proposalItem.quantity);
	}, [proposalItem?.quantity]);

	if (!item) {
		return <pre>INVALID ITEM</pre>
	}

	const image = item.media?.find((m) => m.type === "image")?.source;
	const thumb = image && processImagePath(image, "thumb");

	const selectOrCreateNewProposal = async (text = undefined, { includeOpenCurrent } = {}) => {
		const result = await Dialog.fire({
			html: text ?? "",
			icon: "warning",
			showCloseButton: true,
			showDenyButton: true,
			confirmButtonText: "Create New",
			denyButtonText: `Select Existing`,
			denyButtonColor: '#09f',
			showCancelButton: includeOpenCurrent,
			cancelButtonText: 'Open Current',
		});

		if (result.isDismissed) {
			if (result.dismiss.toString() === "cancel") {
				SidebarContext.open(SidebarContentType.Proposal);
			}
			return false;
		}
		else if (result.isConfirmed) {
			return await createNewProposal();
		} else if (result.isDenied) {
			return new Promise(resolve => {
				let dialogInstance;

				DialogContext
					.open({
						heading: `Select Proposal`,
						content: <ProposalSelector onSelect={async p => {
							dialogInstance.close();
							setLoadingMessage("Updating & Loading Proposal");
							await fetchAndSetCurrent(p.id);
							resolve(p);
						}} />,
						withInstance: true,
					})
					.then(({ instance }) => {
						dialogInstance = instance;
					});
			});
		}
	};
	
	const getProposalModalText = proposal => {
		const proposalModalTexts = {
			noProposal: "Please select or create a proposal.",
			pending: `</br></br>Proposal "${proposal?.name}" is pending and can't be updated.</br></br>If you would like to change this Proposal, it must be returned to a Draft state.`,
			approved: `</br></br>Proposal "${proposal?.name}" is approved and can't be updated. </br></br>Please create new Proposal or select an existing one.`
		}
		
		const getProposalModalText = () => {
			if(!proposal) return proposalModalTexts.noProposal;
			if(proposal?.consented) return proposalModalTexts.pending;
			if(proposal?.approved) return proposalModalTexts.approved;
		}
		
		return getProposalModalText();
	}

	// Find proposal item
	const setProperty = async (property, value) => {
		/** @type Proposal */
		let selectedProposal = currentProposal;

		if (!selectedProposal || selectedProposal?.consented) {
			const text = selectedProposal?.consented ? getProposalModalText(selectedProposal) : "Please select or create a proposal first.";
			const selectedOrCreatedProposal = await selectOrCreateNewProposal(text, { includeOpenCurrent: !!selectedProposal });
			if (selectedOrCreatedProposal) {
				selectedProposal = selectedOrCreatedProposal;
				proposalItem.proposal = selectedProposal.id;
			}
			else {
				// closed or select existing
				setQuantityDisplay(cur => cur - 1);
				return;
			}
		}
		
		if (selectedProposal?.approved) {
			const result = await selectOrCreateNewProposal(`Proposal "${selectedProposal?.name}" is approved and can't be updated. </br></br>Please create new Proposal or select an existing one.`);
			!result && setQuantityDisplay(proposalItem.quantity || undefined);
			return;
		}
		
		setLoadingMessage(undefined);
		
		// If quantity 0, prompt to remove item
		const confirmedRemoved = property === "quantity" && +value === 0;
		property === "quantity" && trackEvent("Proposal", value ? "add" : "remove", item.common_sku);

		if (confirmedRemoved) {
			try {
				await currentProposal.removeItem(proposalItem);
			}
			catch (e) {
				if (!(e instanceof AxiosError && e.response.status === 404)) {
					console.error("Failed to remove item", e);
				}
			}

			currentProposal.items = currentProposal.items.filter(i => i.id !== proposalItem.id);
			setCurrentProposal(currentProposal);
		}
		else {
			proposalItem.setProperty(property, value);

			if (!proposalItem.id) {
				setIsSaving(true);
				try {
					await proposalItem.save();
					selectedProposal.addItem(proposalItem);
					setCurrentProposal(selectedProposal);
					SidebarContext.open(SidebarContentType.Proposal);
				} catch (e) {
					console.error("Failed to add to proposal", e);
				}
				setIsSaving(false);
			}
			else {
				try {
					await proposalItem.saveProperty(property);
				} catch (e) {
					if (!(e instanceof AxiosError && e.response.status === 404)) {
						console.error("Failed to remove from proposal", e);
					}
				}
			}
		}

		forceUpdateRerenderFlag();
	}

	const addToProposal_click = async () => {
		aboutToUpdateQuantity.current ??= proposalItem.quantity ? +proposalItem.quantity : 0;

		aboutToUpdateQuantity.current = isSingleUse ? +!aboutToUpdateQuantity.current : aboutToUpdateQuantity.current + 1;

		clearTimeout(aboutToUpdateDebounce.current);
		aboutToUpdateDebounce.current = setTimeout(async () => {
			if ((proposalItem.quantity ?? 0) === aboutToUpdateQuantity.current) {
				return;
			}
			setAddingToProposal(true);
			if (isSingleUse && !aboutToUpdateQuantity.current) {
				aboutToUpdateQuantity.current = -1;
			}
			await setProperty("quantity", aboutToUpdateQuantity.current);
			setAddingToProposal(false);
			
			aboutToUpdateQuantity.current = undefined;
		}, 500);

		setQuantityDisplay(isSingleUse && !aboutToUpdateQuantity.current ? -1 : aboutToUpdateQuantity.current);
	};


	if (mode === "button") {
		let label = quantityDisplay ? `In Order (${quantityDisplay})` : "Add to Order";
		if (isSingleUse) {
			label = proposalItem.id && !quantityDisplay ? "Saved in Order" : quantityDisplay === 1 ? "In Order" : "Add to Order";
		}

		return (
			<div
				data-component="ProposalItemActions"
				data-type="button"
				className={[addingToProposal && "processing"].filter(f => f).join(" ")}
			>
				<Button
					type="button"
					variant="primary"
					className="add-to-proposal"
					onClick={addToProposal_click}
					quantity={quantityDisplay}
				>
					{isSingleUse && !!quantityDisplay && inOrderIcon && (inOrderIcon)}
					{isSingleUse && proposalItem.id && !quantityDisplay && savedInOrderIcon && (savedInOrderIcon)}
					{label}
				</Button>
			</div>
		);
	}

	const itemPrice = item?.retail_price
		?? item?.["data:retail_price"]
		?? proposalItem?.price
		?? item?.["data:sell_price"];
	
	isLocked ??= access === "view";
	
	const ItemActions = () => {
		if (Proposal.CanView(catalog) && itemPrice != null) {
			return (
				<>
					<QuantityInput
						key={"qty" + proposalItem?.quantity + flag}
						value={proposalItem?.quantity}
						onChange={e => setProperty("quantity", e)}
						isLocked={isLocked}
						maximumAmount={maximumQuantity}
					/>
					<NotesInput
						key={"note" + proposalItem.id + flag}
						value={proposalItem?.notes}
						title={
							proposalItem?.item?.name ||
							proposalItem?.item?.description ||
							proposalItem?.item?.common_sku
						}
						isLocked={isLocked}
						image={thumb}
						onChange={e => setProperty("notes", e)}
					/>
				</>
			);
		}
	}

	const classes = [
		'proposalItemActions',
		currentProposal && "hasProposal",
		(proposalItem.quantity != null) && "on-proposal",
		isSaving && "isSaving",
		isLocked && "locked",
	].filter(f => f);


	return (
		<div data-component="ProposalItemActions" className={classes.join(' ')}>
			<LoadMask show={loadingMessage} message={loadingMessage} />
			<ItemActions />
		</div>
	);
}
